import React, { useCallback, useEffect, useState } from "react";
import { clsx } from "clsx";
import { useParams } from "react-router-dom";
import {
    Button,
    Dropdown,
    Input,
    Menu,
    Modal,
    Spin,
    Switch,
    Tag,
    Tooltip,
    TableView,
    TableViewColumn,
    TableViewRow,
    message,
} from "../../../design-system";
import { MoreOutlined, WarningOutlined } from "@ant-design/icons";
import {
    deactivateUser,
    deleteProject,
    getOrganization,
    inviteOrganizationUsers,
    listOrganizationProjects,
    listOrganizationUsers,
    reactivateUser,
    resendInvitation,
    setUserRoles,
    updateOrganization,
} from "../../../common/api/AdminDashboardClient";
import {
    AdminProjectSummary,
    Organization,
    OrganizationStatus,
    OrganizationUser,
} from "../../../types/AdminDashboardTypes";
import { handleError } from "../../../common/ErrorHandling";
import { formatDateMonthName, formatDateTime, getRelativeTime } from "../../date/DateOp";
import { useImpersonateOrganization } from "../useImpersonateOrganization";
import { useOrganizationStatus } from "./useOrganizationStatus";
import { InviteUsersModal } from "../../../common/components/InviteUsersModal";
import { UserRole, UserStatus } from "../../../types/UserGroup";
import classes from "./OrganizationsView.module.less";

const SECTIONS = [
    { key: "details", label: "Details" },
    { key: "users", label: "Users" },
    { key: "projects", label: "Projects" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const projectColumns: TableViewColumn[] = [
    { key: "name", label: "Name" },
    { key: "type", label: "Type" },
    { key: "status", label: "Status" },
    { key: "owner", label: "Owner" },
    { key: "created", label: "Created" },
    { key: "actions", label: "", width: "110px" },
];

const userColumns: TableViewColumn[] = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "admin", label: "Admin" },
    { key: "status", label: "Status" },
    { key: "created", label: "Created" },
    { key: "lastActivity", label: "Last activity" },
    { key: "actions", label: "", width: "56px" },
];

/** Wristband invitation links expire after 7 days. */
const INVITE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const inviteExpired = (user: OrganizationUser) =>
    user.status === UserStatus.WAITING &&
    !!user.invitedAt &&
    Date.now() - new Date(user.invitedAt).getTime() > INVITE_EXPIRY_MS;

const STATUS_TAGS: Record<string, { label: string; color: string }> = {
    [UserStatus.CREATED]: { label: "Created", color: "gold" },
    [UserStatus.WAITING]: { label: "Waiting", color: "blue" },
    [UserStatus.ACTIVE]: { label: "Active", color: "green" },
    [UserStatus.INACTIVE]: { label: "Inactive", color: "red" },
};

const statusPill = (user: OrganizationUser) => {
    if (inviteExpired(user)) {
        return <Tag color="orange">Invite expired</Tag>;
    }
    const tag = STATUS_TAGS[user.status];
    return tag ? <Tag color={tag.color}>{tag.label}</Tag> : <Tag>{user.status}</Tag>;
};

/** How long ago, with the exact moment on hover. */
const age = (date: string) => (
    <Tooltip title={formatDateTime(date)}>
        <span>{getRelativeTime(date)}</span>
    </Tooltip>
);

/**
 * While an invitation is pending, the interesting moment is when it was last
 * sent, with a warning once it is old enough to have expired; after that,
 * when the user last signed in.
 */
const lastActivity = (user: OrganizationUser) => {
    if (user.status === UserStatus.WAITING) {
        return (
            <>
                {user.invitedAt ? age(user.invitedAt) : ""}
                {inviteExpired(user) && (
                    <Tooltip title="Invitation sent more than 7 days ago; its link has likely expired.">
                        <WarningOutlined className={classes["invite-warning"]} />
                    </Tooltip>
                )}
            </>
        );
    }
    return user.lastLoginAt ? age(user.lastLoginAt) : "";
};

export const OrganizationDetailView: React.FC = () => {
    const { orgId } = useParams();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [users, setUsers] = useState<OrganizationUser[] | null>(null);
    const [projects, setProjects] = useState<AdminProjectSummary[] | null>(null);
    const [section, setSection] = useState<SectionKey>("details");
    const [openingId, setOpeningId] = useState<number>();
    const [deletingId, setDeletingId] = useState<number>();
    const [inviting, setInviting] = useState(false);
    const [resendingId, setResendingId] = useState<number>();
    const [statusPendingId, setStatusPendingId] = useState<number>();
    const [rolesPendingId, setRolesPendingId] = useState<number>();
    const [nameDraft, setNameDraft] = useState("");
    const [savingName, setSavingName] = useState(false);
    const { impersonate } = useImpersonateOrganization();
    const { toggleStatus, pendingId } = useOrganizationStatus(setOrganization);

    const id = Number(orgId);

    const loadUsers = useCallback(() => {
        listOrganizationUsers(id).then(setUsers).catch(handleError);
    }, [id]);

    const loadProjects = useCallback(() => {
        listOrganizationProjects(id).then(setProjects).catch(handleError);
    }, [id]);

    useEffect(() => {
        getOrganization(id).then(setOrganization).catch(handleError);
        loadProjects();
        loadUsers();
    }, [id, loadProjects, loadUsers]);

    // Re-seed the name draft whenever the loaded organization changes.
    useEffect(() => {
        setNameDraft(organization?.name ?? "");
    }, [organization?.name]);

    const saveName = async () => {
        setSavingName(true);
        try {
            setOrganization(await updateOrganization(id, { name: nameDraft.trim() }));
            message.success("Name updated.");
        } catch (error) {
            handleError(error);
        } finally {
            setSavingName(false);
        }
    };

    /** Opening a project means being in its org: impersonate, then land there. */
    const openProject = async (project: AdminProjectSummary) => {
        setOpeningId(project.id);
        try {
            await impersonate(id, `/project/${project.id}`);
        } finally {
            setOpeningId(undefined);
        }
    };

    const removeProject = async (project: AdminProjectSummary) => {
        setDeletingId(project.id);
        try {
            await deleteProject(project.id);
            message.success("Project deleted.");
            loadProjects();
        } catch (error) {
            handleError(error);
        } finally {
            setDeletingId(undefined);
        }
    };

    const confirmDeleteProject = (project: AdminProjectSummary) => {
        Modal.confirm({
            title: `Delete ${project.name.trim() || "Unnamed project"}?`,
            content:
                "The project and everything in it (analyses, documents, RFx, deals) " +
                "disappears for every member of the organization.",
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: () => removeProject(project),
        });
    };

    const projectMenu = (project: AdminProjectSummary) => (
        <Menu>
            <Menu.Item key="delete" danger onClick={() => confirmDeleteProject(project)}>
                Delete
            </Menu.Item>
        </Menu>
    );

    const projectRow = (project: AdminProjectSummary): TableViewRow => ({
        key: String(project.id),
        searchText: project.name.trim() || "Unnamed project",
        values: {
            name: project.name.trim() || "Unnamed project",
            type: project.type,
            status: project.status,
            owner: project.ownerName ?? "Unassigned",
            created: age(project.recordCreationDate),
            actions: (
                <>
                    <Button
                        type="link"
                        size="small"
                        loading={openingId === project.id}
                        onClick={() => openProject(project)}
                    >
                        Open
                    </Button>
                    <Dropdown overlay={projectMenu(project)} trigger={["click"]}>
                        <Button
                            size="small"
                            type="text"
                            loading={deletingId === project.id}
                            icon={<MoreOutlined />}
                        />
                    </Dropdown>
                </>
            ),
        },
    });

    const resend = async (user: OrganizationUser) => {
        setResendingId(user.id);
        try {
            await resendInvitation(user.id);
            message.success(`Invitation sent to ${user.email}.`);
            loadUsers();
        } catch (error) {
            handleError(error);
        } finally {
            setResendingId(undefined);
        }
    };

    const setStatus = async (
        user: OrganizationUser,
        action: (userId: number) => Promise<OrganizationUser>,
        done: string
    ) => {
        setStatusPendingId(user.id);
        try {
            await action(user.id);
            message.success(`${user.email} ${done}.`);
            loadUsers();
        } catch (error) {
            handleError(error);
        } finally {
            setStatusPendingId(undefined);
        }
    };

    const confirmDeactivate = (user: OrganizationUser) => {
        Modal.confirm({
            title: `Deactivate ${user.email}?`,
            content:
                "They are logged out immediately and cannot sign in again until " +
                "they are reactivated.",
            okText: "Deactivate",
            okButtonProps: { danger: true },
            onOk: () => setStatus(user, deactivateUser, "deactivated"),
        });
    };

    /**
     * Org admin is the one role the dashboard grants or removes; the server
     * keeps everything else the user holds (superadmin included) through
     * the edit. Roles live in our database only, so this needs no Wristband
     * tenant and works while the org is inactive.
     */
    const setAdmin = async (user: OrganizationUser, admin: boolean) => {
        setRolesPendingId(user.id);
        try {
            await setUserRoles(user.id, admin ? [UserRole.ADMIN] : []);
            message.success(`${user.email} is ${admin ? "now" : "no longer"} an org admin.`);
            loadUsers();
        } catch (error) {
            handleError(error);
        } finally {
            setRolesPendingId(undefined);
        }
    };

    const adminCell = (user: OrganizationUser) => (
        <Switch
            size="small"
            checked={user.roles.includes(UserRole.ADMIN)}
            loading={rolesPendingId === user.id}
            onChange={(checked: boolean) => setAdmin(user, checked)}
        />
    );

    // Deactivation is for users who can sign in: an invited user who never
    // accepted has no account to switch off (and their invitation would
    // survive it anyway), so CREATED and WAITING users get the invite and
    // role actions but no status switch.
    const userMenu = (user: OrganizationUser) => (
        <Menu>
            {/* Only users still waiting on an invitation get the invite
                action; an active user has nothing to accept. */}
            {(user.status === UserStatus.CREATED || user.status === UserStatus.WAITING) && (
                <Menu.Item key="resend" onClick={() => resend(user)}>
                    {user.status === UserStatus.CREATED ? "Send invite" : "Resend invite"}
                </Menu.Item>
            )}
            {user.status === UserStatus.ACTIVE && (
                <Menu.Item key="deactivate" danger onClick={() => confirmDeactivate(user)}>
                    Deactivate
                </Menu.Item>
            )}
            {user.status === UserStatus.INACTIVE && (
                <Menu.Item
                    key="reactivate"
                    onClick={() => setStatus(user, reactivateUser, "reactivated")}
                >
                    Reactivate
                </Menu.Item>
            )}
        </Menu>
    );

    const userRow = (user: OrganizationUser): TableViewRow => ({
        key: String(user.id),
        searchText: user.name.trim() || user.email,
        values: {
            name: user.name.trim() || user.email,
            email: user.email,
            admin: adminCell(user),
            status: statusPill(user),
            created: user.recordCreationDate ? formatDateMonthName(user.recordCreationDate) : "",
            lastActivity: lastActivity(user),
            // Every user action goes through the org's Wristband tenant,
            // which is switched off while the org is inactive; reactivate
            // the org first.
            actions:
                organization?.status === OrganizationStatus.ACTIVE ? (
                    <Dropdown overlay={userMenu(user)} trigger={["click"]}>
                        <Button
                            size="small"
                            type="text"
                            loading={resendingId === user.id || statusPendingId === user.id}
                            icon={<MoreOutlined />}
                        />
                    </Dropdown>
                ) : null,
        },
    });

    if (organization === null || users === null) {
        return (
            <div className={classes.container}>
                <Spin />
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={clsx(classes.header, classes.headerCompact)}>
                <h1 className={classes.title}>
                    {organization.name}
                    {organization.status === OrganizationStatus.ACTIVE ? (
                        <Tag className={classes.statusTag} color="green">
                            Active
                        </Tag>
                    ) : (
                        <Tag className={classes.statusTag} color="red">
                            Inactive
                        </Tag>
                    )}
                </h1>
                <div className={classes.actions}>
                    <Button
                        danger={organization.status === OrganizationStatus.ACTIVE}
                        loading={pendingId === organization.id}
                        onClick={() => toggleStatus(organization)}
                    >
                        {organization.status === OrganizationStatus.ACTIVE
                            ? "Deactivate"
                            : "Activate"}
                    </Button>
                </div>
            </div>
            <div className={classes.subtitle}>{organization.domain}</div>
            <div className={classes.workspace}>
                <Menu
                    className={classes.sections}
                    mode="inline"
                    inlineIndent={12}
                    selectedKeys={[section]}
                    onSelect={({ key }) => setSection(key as SectionKey)}
                    items={[
                        {
                            key: "organization",
                            type: "group",
                            label: "Organization",
                            children: SECTIONS.map(({ key, label }) => ({ key, label })),
                        },
                    ]}
                />
                <div className={classes["section-content"]}>
                    {section === "details" && (
                        <>
                            <div className={classes.sectionHeader}>
                                <h2 className={classes.sectionTitle}>Details</h2>
                            </div>
                            <div className={classes.detailsField}>
                                <label className={classes.detailsLabel} htmlFor="org-display-name">
                                    Display name
                                </label>
                                <div className={classes.detailsRow}>
                                    <Input
                                        id="org-display-name"
                                        value={nameDraft}
                                        onChange={(event) => setNameDraft(event.target.value)}
                                    />
                                    <Button
                                        type="primary"
                                        loading={savingName}
                                        disabled={
                                            !nameDraft.trim() ||
                                            nameDraft.trim() === organization.name
                                        }
                                        onClick={saveName}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                            <div className={classes.detailsField}>
                                <span className={classes.detailsLabel}>Wristband tenant name</span>
                                <span>{organization.domain}</span>
                            </div>
                        </>
                    )}
                    {section === "users" && (
                        <>
                            <div className={classes.sectionHeader}>
                                <h2 className={classes.sectionTitle}>Users</h2>
                                <Button
                                    size="small"
                                    // The backend refuses invites into an inactive
                                    // tenant anyway (no active tenant to invite
                                    // into); the disabled button says so up front.
                                    disabled={organization.status === OrganizationStatus.INACTIVE}
                                    onClick={() => setInviting(true)}
                                >
                                    Invite users
                                </Button>
                            </div>
                            <InviteUsersModal
                                title="Invite users"
                                open={inviting}
                                onClose={() => setInviting(false)}
                                onSent={loadUsers}
                                invite={(invites) => inviteOrganizationUsers(id, { invites })}
                            />
                            <TableView
                                columns={userColumns}
                                rows={users.map(userRow)}
                                searchPlaceholder="Search users"
                            />
                        </>
                    )}
                    {section === "projects" &&
                        (projects === null ? (
                            <Spin />
                        ) : (
                            <>
                                <div className={classes.sectionHeader}>
                                    <h2 className={classes.sectionTitle}>Projects</h2>
                                </div>
                                <TableView
                                    columns={projectColumns}
                                    rows={projects.map(projectRow)}
                                    searchPlaceholder="Search projects"
                                />
                            </>
                        ))}
                </div>
            </div>
        </div>
    );
};
